import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { motion } from "framer-motion";

const ItemTypes = {
  QUESTION: "question",
};

const DraggableMcq = ({
  id,
  index,
  question,
  options,
  correctAnswer,
  moveQuestion,
  onChange,
  remove,
}) => {
  const ref = useRef(null);
  const dragHandleRef = useRef(null);

  const [, drop] = useDrop({
    accept: ItemTypes.QUESTION,
    hover(item) {
      if (item.index !== index) {
        moveQuestion(item.index, index);
        item.index = index;
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.QUESTION,
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref)); // Apply drop behavior
  drag(dragHandleRef); // Only allow dragging via handle

  return (
    <div
      ref={ref}
      className={`flex flex-col sm:flex-row gap-4 w-full items-center mt-4 bg-gray-200 p-4 shadow-lg rounded-lg ${
        isDragging ? "opacity-50" : "opacity-100"
      }`}
    >
      <div className="w-full space-y-4 p-4 border rounded-lg bg-white shadow">
        {/* Top Section: Drag + Number + Question */}
        <div className="flex items-start gap-3">
          {/* Drag Handle */}
          <div
            ref={dragHandleRef}
            className="cursor-grab p-2 hover:bg-gray-200 rounded-lg"
          >
            <i className="fa fa-grip-vertical text-gray-600"></i>
          </div>

          {/* Question Number */}
          <span className="text-gray-700 font-medium pt-2">{index + 1}.</span>

          {/* Question Input */}
          <textarea
            placeholder="Enter question"
            value={question}
            onChange={(e) => onChange(index, "question", e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg h-12 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Options Section */}
        <div className="space-y-2 pl-10">
          {options.map((option, i) => (
            <div key={i} className="flex items-center gap-3">
              <input
                type="radio"
                name={`correctAnswer-${index}`}
                checked={correctAnswer === i}
                onChange={() => onChange(index, "correctAnswer", i)}
                className="text-blue-600"
              />
              <input
                type="text"
                value={option}
                onChange={(e) =>
                  onChange(index, `options.${i}`, e.target.value)
                }
                className="border px-3 py-2 rounded-lg w-1/2 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder={`Option ${i + 1}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Remove Button */}
      <motion.button
        onClick={() => remove(index)}
        className="flex justify-center items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <i className="fa fa-trash-can"></i>
        Remove
      </motion.button>
    </div>
  );
};

export default DraggableMcq;
